import { useEffect, useRef, useState } from 'react';

import { DateTime } from 'luxon';

import { useAxiosRequest } from '@admin/hooks/use_axios_request';

import { fetchOrder } from './api';
import { type OpsMapParams } from './url_state';

interface UseOrderRedirectOptions {
  ready: boolean;
  params: OpsMapParams;
  go: (params: OpsMapParams, options?: { preserveFocus?: boolean }) => void;
}

export const useOrderRedirect = ({ ready, params, go }: UseOrderRedirectOptions) => {
  const [request, setRequest] = useState<ReturnType<typeof fetchOrder> | undefined>();
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!ready || redirectedRef.current) return;

    if (params.region && params.dispatch) return;
    if (!params.order || !params.account_id) return;

    setRequest(fetchOrder(params.account_id, params.order));
  }, [ready, params.account_id, params.dispatch, params.order, params.region]);

  const { response } = useAxiosRequest(request);

  useEffect(() => {
    if (!response?.data || redirectedRef.current) return;

    const order = response.data;
    const nextParams: OpsMapParams = { ...params };

    if (!params.region || !params.dispatch) {
      if (order.dispatch_id) nextParams.dispatch = order.dispatch_id;
      if (order.scheduled) nextParams.date = DateTime.fromISO(order.scheduled).toFormat('yyyy-MM-dd');
      if (order.region?.id) nextParams.region = order.region.id;
    }

    nextParams.account_id = undefined;
    if (!params.order) nextParams.order = undefined;

    redirectedRef.current = true;
    go(nextParams, { preserveFocus: true });
  }, [go, params, response]);
};
